import { t } from 'i18next';
import { batch } from 'react-redux';
import type { NavigateFunction } from 'react-router-dom';
import {
  setAlarmCreateModel,
  setAlarmAllSteps,
  setIsLoading,
  setTypes,
  clearAlarmCreationData,
  setActiveAlarmsModalData,
  setActiveAlarms,
} from '.';
import { getEmergencyTypes } from '../../../apis/authApi/authAPI';
import { createMessage, getActiveAlarms } from '../../../apis/chatAPI';
import { saveLocation } from '../../../apis/locationAPI';
import type { AppThunk } from '../../../store';
import type { ConfirmationOptions } from '../../../utils/ConfirmationServiceContext/confirmationContext';
import { AlarmStep, AlarmStepLinks } from '../../../utils/enums';
import Geocoder from '../../../utils/geocoder';
import { getItem } from '../../../utils/storage';
import type { CreateMessageModel } from '../../Chat/models';
import { fetchChats } from '../../ChatsList/chatListSlice/actionCreators';
import type { Location } from '../../GroupDetail/groupDetailSlice/types';
import { fetchGroups } from '../../GroupsList/groupsSlice/actionCreators';
import { setIsError, setIsInternalLoading } from '../../Support/supportSlice';
import type { ActiveAlarmsModal, EmergencyType } from './types';
import { isNil } from 'lodash';
import { getAlarmName } from '../../../components/AlarmItem/helpers';
import { isTruthyIncludingZero } from '../../../utils/isTruthyIncludingZero';
import { getGroups } from '../../../apis/groupsAPI';
import { getAppLanguage } from '../../../i18n';

export const fetchActiveAlarms =
  (groupIds: number[], callback?: (data: ActiveAlarmsModal) => void): AppThunk =>
  async dispatch => {
    try {
      const now = new Date();
      now.setHours(now.getHours() - 1);
      const alarms = await getActiveAlarms({
        groupIds,
        take: 100,
        minSentDate: now.toISOString(),
      });
      if (alarms?.items?.length) {
        const newActiveAlarmsModalData = {
          isOpen: true,
          items: alarms.items,
        };
        dispatch(setActiveAlarmsModalData(newActiveAlarmsModalData));
        if (callback) {
          callback(newActiveAlarmsModalData);
        }
      }
    } catch (error) {
      console.log('error log ', error);
    }
  };

export const fetchAllActiveAlarms = (): AppThunk => async dispatch => {
  try {
    const alarms = await getActiveAlarms({ take: 100 });
    dispatch(setActiveAlarms(alarms?.items ?? []));
  } catch (error) {
    console.error('fetchAllActiveAlarms error', error);
  }
};

export const getEmergencyTypesAction =
  (showLoading = true): AppThunk =>
  async dispatch => {
    try {
      if (showLoading) dispatch(setIsLoading(true));
      // eslint-disable-next-line no-async-promise-executor
      const promise = new Promise<EmergencyType[]>(async resolve => {
        const res = await getEmergencyTypes();
        dispatch(fetchGroups(undefined, false, () => resolve(res)));
      });

      promise.then(res => {
        batch(() => {
          dispatch(setTypes(res));
          if (showLoading) dispatch(setIsLoading(false));
        });
      });
    } catch (error) {
      console.log('error log ', error);
      batch(() => {
        dispatch(setIsInternalLoading(false));
        dispatch(setIsError(`${error}`));
      });
    }
  };

export const sendAlarm =
  (
    messageModel: CreateMessageModel,
    location: Location | null | undefined,
    navigate: NavigateFunction,
    customText: string | ''
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      const messageModelObject = { ...messageModel };
      const activeAlarmsModalData = getState().alarm.activeAlarmsModalData;
      let stopAction = false;
      if (!activeAlarmsModalData.isOpen && messageModel.groupIds) {
        const allGroups = await getGroups();

        const allSelectedSubOrganizationGroups = allGroups.filter(
          group =>
            messageModel.groupIds?.includes(group.id) &&
            isTruthyIncludingZero(group.subOrganizationID)
        );

        if (allSelectedSubOrganizationGroups?.length) {
          await dispatch(
            fetchActiveAlarms(
              allSelectedSubOrganizationGroups.map(group => group.id),
              (data: ActiveAlarmsModal) => {
                dispatch(
                  setActiveAlarmsModalData({
                    ...data,
                    sendAlarmProps: {
                      messageModel,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      location,
                      customText,
                    },
                  })
                );
                stopAction = true;
              }
            )
          );
        }
      }
      if (stopAction) {
        dispatch(setIsLoading(false));
        return;
      }
      dispatch(setIsLoading(true));
      if (location && !isNil(location.latitude)) {
        const result = await Geocoder({
          latitude: location.latitude,
          longitude: location.longitude,
        });
        const locName = result?.addressName
          ? result?.addressName
          : `${location.latitude} | ${location.longitude}`;

        const loc = {
          name: locName,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
        };

        const locationResult = await saveLocation(loc);
        messageModelObject.locationId = locationResult?.id;
      }
      messageModelObject.text = customText;
      const res = await createMessage(messageModelObject);
      if (res) {
        batch(async () => {
          dispatch(setIsLoading(false));
          dispatch(fetchChats({}));
        });
        dispatch(fetchAllActiveAlarms());
        navigate(`/message/${res.id}`);
        setTimeout(() => {
          dispatch(clearAlarmCreationData());
        }, 3000);
      }
    } catch (error) {
      console.log(error);
      batch(() => {
        dispatch(setIsInternalLoading(false));
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
    }
  };

const getLocation = async () => {
  if (navigator.geolocation) {
    return new Promise<Location | null>(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            name: `${position.coords.latitude} | ${position.coords.longitude}`,
          });
        },
        error => {
          console.error('Geolocation Error' + error.code, error.message);
          resolve(null);
        },
        { maximumAge: 5000, enableHighAccuracy: false, timeout: 5000 }
      );
    });
  } else {
    console.log('geolocation is unavailable on the device.');
    return null;
  }
};

export const calculateAllAlarmSteps =
  (alarmType: EmergencyType): AppThunk =>
  async dispatch => {
    const allSteps = [AlarmStep.SelectType];
    if (!alarmType.NoGroup) allSteps.push(AlarmStep.SelectGroups);
    if (alarmType.allowCustomizedText) allSteps.push(AlarmStep.AddCustomText);
    if (!alarmType.SkipMap && !alarmType.disableLocationSharing)
      allSteps.push(AlarmStep.SelectLocation);
    dispatch(setAlarmAllSteps(allSteps));
  };

export const handleNavigate =
  (
    userId: number | undefined,
    groupIds: number[],
    alarmTypeItem: EmergencyType | null,
    navigate: NavigateFunction,
    currentStep: AlarmStep,
    confirm: (options: ConfirmationOptions) => Promise<void>,
    location?: Location | null,
    customText?: string | ''
  ): AppThunk =>
  async dispatch => {
    if (!userId || !alarmTypeItem) {
      console.log('handleNavigate at actionCreators.ts: incorrect args');
      return;
    }
    dispatch(setAlarmCreateModel(alarmTypeItem));
    if (!alarmTypeItem.NoGroup && currentStep < AlarmStep.SelectGroups)
      navigate(AlarmStepLinks.SelectGroups);
    else if (alarmTypeItem.allowCustomizedText && currentStep < AlarmStep.AddCustomText)
      navigate(AlarmStepLinks.AddCustomText);
    else if (
      !alarmTypeItem.SkipMap &&
      !alarmTypeItem.disableLocationSharing &&
      currentStep < AlarmStep.SelectLocation
    )
      navigate(AlarmStepLinks.SelectLocation);
    else {
      // if all other steps are finished proceed with sending

      //prepare messageModel
      const groupIdsList = groupIds?.length
        ? groupIds
        : alarmTypeItem?.Groups
          ? alarmTypeItem.Groups.map(g => g.id)
          : [];
      const messageModel: CreateMessageModel = {
        text: undefined,
        senderId: userId,
        groupIds: groupIdsList,
        emergencyTypeId: alarmTypeItem.ID,
        subOrganisationIDForEmergencyMessage: alarmTypeItem.subOrganisationIDForEmergencyMessage!,
        type: 2,
        recipientIds: null,
        subject: null,
      };
      //ask for user confirmation if needed and then send
      if (alarmTypeItem.warningStep) {
        const currentLanguage = getAppLanguage();
        const alarmName = getAlarmName(alarmTypeItem, currentLanguage);
        confirm({
          title: 'messages_confirmation',
          description: t('alarm_new_alarm_confirmation', {
            alarmName,
          }),
          onSubmit: async () => {
            dispatch(setIsLoading(true));
            // if location wasnt provided try to get it automatically
            if (!alarmTypeItem.disableLocationSharing && !location) {
              location = await getLocation();
            }
            dispatch(sendAlarm(messageModel, location, navigate, customText || ''));
          },
          onCancel: () => {
            dispatch(setIsLoading(false));
          },
          confirmText: 'confirm',
          cancelText: 'cancel',
        });
      } else {
        dispatch(setIsLoading(true));
        // if location wasnt provided try to get it automatically
        if (!alarmTypeItem.disableLocationSharing && !location) {
          location = await getLocation();
        }
        dispatch(sendAlarm(messageModel, location, navigate, customText || ''));
      }
    }
  };

export const showWarningConfirmationModal =
  (
    userId: number | undefined,
    groupIds: number[],
    alarmTypeItem: EmergencyType | null,
    navigate: NavigateFunction,
    confirm: (options: ConfirmationOptions) => Promise<void>,
    location?: Location | null,
    customText?: string | ''
  ): AppThunk =>
  async dispatch => {
    if (!userId || !alarmTypeItem) {
      console.warn('handleNavigate at actionCreators.ts: incorrect args');
      return;
    }

    dispatch(setAlarmCreateModel(alarmTypeItem));
    //prepare messageModel
    const messageModel: CreateMessageModel = {
      text: undefined,
      senderId: userId,
      groupIds: groupIds,
      emergencyTypeId: alarmTypeItem.ID,
      subOrganisationIDForEmergencyMessage: alarmTypeItem.subOrganisationIDForEmergencyMessage!,
      type: 2,
      recipientIds: null,
      subject: null,
    };
    //ask for user confirmation if needed and then send
    const currentLanguage = getAppLanguage();
    const alarmName = getAlarmName(alarmTypeItem, currentLanguage);
    confirm({
      title: 'messages_confirmation',
      description: t('alarm_new_alarm_confirmation', {
        alarmName,
      }),
      onSubmit: () => {
        dispatch(setIsLoading(true));
        if (!location) {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              async position => {
                const locationObject = {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracy: position.coords.accuracy,
                } as Location;
                dispatch(sendAlarm(messageModel, locationObject, navigate, customText || ''));
              },
              error => {
                dispatch(sendAlarm(messageModel, undefined, navigate, customText || ''));
                console.error('Geolocation Error' + error.code, error.message);
              },
              { maximumAge: 5000, enableHighAccuracy: true, timeout: 10000 }
            );
          } else {
            dispatch(sendAlarm(messageModel, undefined, navigate, customText || ''));
            console.warn("Browser doesn't support Geolocation");
          }
        } else {
          dispatch(sendAlarm(messageModel, location, navigate, customText || ''));
        }
      },
      onCancel: () => {
        dispatch(setIsLoading(false));
      },
      confirmText: 'confirm',
      cancelText: 'cancel',
    });
  };

/*

GUIDELINES ON HOW TO ADD PAGE TO ALARM CREATE PROCESS / CHANGE ALARM STEPS IN PLACES:

these steps should help you to quickly and easily add new pages to the alarm creation:

1. Create a component for the page you want to add.
You can extract alarm information and current step / all steps numbers using AppSelector and alarmSlice store (../index.ts)
3. Add the property provided by your page to received alarmType prop. Don't forget to also add it to the corresponding type.
4. Call the handleNavigate function on "proceed" click at your component.
5. Add link to the step component to AlarmStepLinks at enums.ts
6. Add the name of step to AlarmStep at enums.ts
7. add "if" check to handleNavigate function (two lines of code one of which is almost entirely copied).
   you should add it before "if" block where user is navigated to the step which should be next to your page.
   it should look like that:
    else if (alarmTypeItem.BACKENDPROPERTY && currentStep < AlarmStep.YOURALARMSTEPNAME)
      navigate(AlarmStepLinks.YOURALARMSTEPNAME);
    where BACKENDPROPERTY is property which should be checked to know if the step should be skipped and
    YOURALARMSTEPNAME is the property name which you have added to AlarmStep and AlarmStepLinks.
    this "if" check is used to properly navigate to your page.
8. Add the property provided by your page to messageModel declaration in handleNavigate.

the steps below should help you to quickly change alarm creation steps in places:

1. Change the order of steps in AlarmStep at core-enums.ts and write the corresponding property value numbers. 0 is the first step, 1 is second and so on.
2. Change the order of "if" checks the same way in handleNavigation function before that line:
        const allSteps = calculateAllStepsAmount(emergencyType)
3. Done!
*/
